import { AnalyticsTypeOption } from '@/components/analytics/StatCellValues';
import { usePersistedJsonState } from '@superfunnel/react-hooks';
import {
  AccountAiSettings,
  AccountBrand,
  AccountConfig,
  AccountSubscription,
  AdsAccount,
  AnalyticsFilters,
  Domain,
  DynamicPage,
  EmptyAnalyticsFilters,
  Form,
  GlobalSection,
  LinkedCampaign,
  Quiz,
  SectionType,
  SectionTypeConfig,
  SectionTypeStructureFieldList,
  TeamMember,
} from '@superfunnel/types';
import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { db } from '../Firebase';
import { getAccountAiSettingsDocRef } from '../services/AccountAiSettingsService';
import { getAccountBrandDocRef } from '../services/AccountBrandService';
import { getAccountConfigDocRef } from '../services/AccountConfigService';
import {
  updateAccountAiSettings as updateAccountAiSettingsApi,
  updateAccountBrand as updateAccountBrandApi,
  updateAccountConfig as updateAccountConfigApi,
} from '../services/AccountsService';
import { activeAdsAccountsQuery } from '../services/AdsAccountsService';
import { getDomainsColRef } from '../services/DomainsService';
import { dynamicPagesQuery } from '../services/DynamicPagesService';
import { formsQuery } from '../services/FormsService';
import { globalSectionsQuery } from '../services/GlobalSectionsService';
import { activeLinkedCampaignsQuery } from '../services/LinkedCampaignsService';
import { quizzesQuery } from '../services/QuizService';
import { getAccountSectionTypes } from '../services/SectionTypesService';
import { approvedTeamMembersQuery } from '../services/TeamMembersService';
import { UserContext } from './UserContext';

export type AnalyticsCustomRange = {
  startDate: string;
  endDate: string;
};

export type AnalyticsRange =
  | 'today'
  | 'yesterday'
  | 'last7Days'
  | 'last30Days'
  | 'currentMonth'
  | AnalyticsCustomRange;

interface DashboardContextProps {
  dynamicPages?: QueryDocumentSnapshot<DynamicPage, DynamicPage>[];
  isNewAccount: boolean;
  // selectedDynamicPage?: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
  // setSelectedDynamicPage: (
  //   page: QueryDocumentSnapshot<DynamicPage, DynamicPage> | undefined,
  // ) => void;
  linkedCampaigns?: QueryDocumentSnapshot<LinkedCampaign, LinkedCampaign>[];
  teamMembers?: QueryDocumentSnapshot<TeamMember, TeamMember>[];
  forms?: QueryDocumentSnapshot<Form, Form>[];
  quizzes?: QueryDocumentSnapshot<Quiz, Quiz>[];
  globalSections?: QueryDocumentSnapshot<GlobalSection, GlobalSection>[];

  allDomains?: QueryDocumentSnapshot<Domain, Domain>[];
  domains?: QueryDocumentSnapshot<Domain, Domain>[];
  primaryDomain?: QueryDocumentSnapshot<Domain, Domain>;
  adsAccounts?: QueryDocumentSnapshot<AdsAccount, AdsAccount>[];

  sectionTypeConfigs?: QueryDocumentSnapshot<SectionTypeConfig>[];
  getSectionTypeConfig: (
    type: SectionType | string,
  ) => SectionTypeConfig | undefined;
  getSectionListItemFieldConfig: (
    sectionType: SectionType | string,
    field: string,
  ) => SectionTypeStructureFieldList | undefined;

  analyticsType: AnalyticsTypeOption | null;
  setAnalyticsType: (type?: AnalyticsTypeOption) => void;

  accountBrand?: AccountBrand;
  updateAccountBrand: (config: Partial<AccountBrand>) => void;

  accountAiSettings?: AccountAiSettings;
  updateAccountAiSettings: (config: Partial<AccountAiSettings>) => void;

  accountConfig?: AccountConfig;
  updateAccountConfig: (config: Partial<AccountConfig>) => void;

  subscription?: DocumentSnapshot<AccountSubscription>;
  hasActiveSubscription: boolean;

  analyticsRange: AnalyticsRange;
  setAnalyticsRange: (range: AnalyticsRange) => void;

  analyticsFilters: AnalyticsFilters;
  setAnalyticsFilters: (filters: AnalyticsFilters) => void;
  resetAnalyticsFilters: () => void;

  // dynamicPageWebsitePreviewToken?: string;
  // fetchDynamicPageWebsitePreviewToken: () => Promise<any>;
}

export const DashboardContext = createContext<DashboardContextProps>({
  isNewAccount: true,
  getSectionTypeConfig: () => undefined,
  getSectionListItemFieldConfig: () => undefined,

  // setSelectedDynamicPage: () => {},
  updateAccountBrand: () => {},
  updateAccountAiSettings: () => {},
  updateAccountConfig: () => {},
  analyticsType: null,
  setAnalyticsType: () => {},
  hasActiveSubscription: false,

  analyticsRange: 'last30Days',
  setAnalyticsRange: () => {},

  analyticsFilters: EmptyAnalyticsFilters,
  setAnalyticsFilters: () => {},
  resetAnalyticsFilters: () => {},

  // fetchDynamicPageWebsitePreviewToken: async () => '',
});

export function DashboardContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);

  const [dynamicPages, setDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [linkedCampaigns, setLinkedCampaigns] =
    useState<QueryDocumentSnapshot<LinkedCampaign, LinkedCampaign>[]>();
  const [forms, setForms] = useState<QueryDocumentSnapshot<Form, Form>[]>();
  const [quizzes, setQuizzes] = useState<QueryDocumentSnapshot<Quiz, Quiz>[]>();
  const [globalSections, setGlobalSections] =
    useState<QueryDocumentSnapshot<GlobalSection, GlobalSection>[]>();
  const [allDomains, setAllDomains] =
    useState<QueryDocumentSnapshot<Domain, Domain>[]>();
  const [accountBrand, setAccountBrand] = useState<AccountBrand>();
  const [accountBrandToSave, setAccountBrandToSave] =
    useState<Partial<AccountBrand>>();
  const [accountAiSettings, setAccountAiSettings] =
    useState<AccountAiSettings>();
  const [accountAiSettingsToSave, setAccountAiSettingsToSave] =
    useState<Partial<AccountAiSettings>>();
  const [accountConfig, setAccountConfig] = useState<AccountConfig>();
  const [accountConfigToSave, setAccountConfigToSave] =
    useState<Partial<AccountConfig>>();
  const [subscription, setSubscription] =
    useState<DocumentSnapshot<AccountSubscription>>();
  const [sectionTypeConfigs, setSectionTypeConfigs] =
    useState<QueryDocumentSnapshot<SectionTypeConfig>[]>();
  const [analyticsRange, setAnalyticsRange] =
    usePersistedJsonState<AnalyticsRange>('analyticsRange', 'last30Days');
  const [analyticsFilters, setAnalyticsFilters] =
    usePersistedJsonState<AnalyticsFilters>(
      'analyticsFilters',
      EmptyAnalyticsFilters,
    );
  const [analyticsType, _setAnalyticsType] =
    useState<AnalyticsTypeOption | null>(
      (localStorage.getItem('analyticsType') as AnalyticsTypeOption) || null,
    );
  const [teamMembers, setTeamMembers] =
    useState<QueryDocumentSnapshot<TeamMember, TeamMember>[]>();
  const [adsAccounts, setAdsAccounts] =
    useState<QueryDocumentSnapshot<AdsAccount, AdsAccount>[]>();

  const domains = useMemo(() => {
    return allDomains?.filter((d) => d.data().status === 'approved');
  }, [allDomains]);

  const primaryDomain = useMemo(() => {
    return (
      domains?.find((d) => d.data().type === 'custom') ||
      domains?.find((d) => d.data().type === 'subdomain')
    );
  }, [domains]);

  const hasActiveSubscription = useMemo(() => {
    const subscriptionData = subscription?.data();
    return (
      subscriptionData?.status === 'active' ||
      subscriptionData?.status === 'on_trial'
    );
  }, [subscription]);

  const resetAnalyticsFilters = useCallback(() => {
    setAnalyticsFilters(EmptyAnalyticsFilters);
  }, [setAnalyticsFilters]);

  const isNewAccount = useMemo(() => {
    const createdAt: Timestamp = selectedAccount?.data().createdAt;
    if (!createdAt) return true;

    const createdDate = createdAt.toDate();
    const isNewThreshold = new Date('2025-11-24T00:00:00Z');

    return createdDate >= isNewThreshold;
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setDynamicPages(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      dynamicPagesQuery(accountId),
      (dynamicPages) => {
        setDynamicPages(dynamicPages.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setLinkedCampaigns(undefined);
      return;
    }

    const accountId = selectedAccount.id;

    const unsubscribe = onSnapshot(
      activeLinkedCampaignsQuery(accountId),
      (linkedCampaigns) => {
        setLinkedCampaigns(linkedCampaigns.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAllDomains(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const domainsColRef = getDomainsColRef(accountId);

    const unsubscribe = onSnapshot(domainsColRef, (domains) => {
      setAllDomains(domains.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setSectionTypeConfigs(undefined);
      return;
    }

    const accountId = selectedAccount.id;

    let relevant = true;
    getAccountSectionTypes(accountId).then((sectionTypes) => {
      if (!relevant) return;
      setSectionTypeConfigs(sectionTypes.docs);
    });

    return () => {
      relevant = false;
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setForms(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(formsQuery(accountId), (forms) => {
      setForms(forms.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setQuizzes(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(quizzesQuery(accountId), (quizzes) => {
      setQuizzes(quizzes.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setGlobalSections(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      globalSectionsQuery(accountId),
      (globalSections) => {
        setGlobalSections(globalSections.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAccountBrand(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountBrandDocRef(accountId),
      (accountBrand) => {
        setAccountBrand(
          accountBrand.exists() ? accountBrand.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAccountAiSettings(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountAiSettingsDocRef(accountId),
      (accountAiSettings) => {
        setAccountAiSettings(
          accountAiSettings.exists() ? accountAiSettings.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAdsAccounts(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      activeAdsAccountsQuery(accountId),
      (adsAccounts) => {
        setAdsAccounts(adsAccounts.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const updateAccountBrand = useCallback((data: Partial<AccountBrand>) => {
    setAccountBrandToSave((d) => ({ ...(d || {}), ...data }));
    setAccountBrand((d) => ({ ...(d || {}), ...data }));
  }, []);

  const updateAccountAiSettings = useCallback(
    (data: Partial<AccountAiSettings>) => {
      setAccountAiSettingsToSave((d) => ({ ...(d || {}), ...data }));
      setAccountAiSettings((d) => ({ ...(d || {}), ...data }));
    },
    [],
  );

  useEffect(() => {
    if (!selectedAccount || !accountBrandToSave) return;

    const debounce = setTimeout(() => {
      updateAccountBrandApi(selectedAccount.id, accountBrandToSave);
      setAccountBrandToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountBrandToSave, selectedAccount]);

  useEffect(() => {
    if (!selectedAccount || !accountAiSettingsToSave) return;

    const debounce = setTimeout(() => {
      updateAccountAiSettingsApi(selectedAccount.id, accountAiSettingsToSave);
      setAccountAiSettingsToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountAiSettingsToSave, selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAccountConfig(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountConfigDocRef(accountId),
      (accountConfig) => {
        setAccountConfig(
          accountConfig.exists() ? accountConfig.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setSubscription(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const subscriptionDocRef = doc(
      collection(db, 'accounts', accountId, 'subscriptions'),
      'current',
    ).withConverter<AccountSubscription>({
      toFirestore: (data: AccountSubscription) => data,
      fromFirestore: (snapshot) => snapshot.data() as AccountSubscription,
    });

    const unsubscribe = onSnapshot(subscriptionDocRef, (subscriptionDoc) => {
      setSubscription(subscriptionDoc.exists() ? subscriptionDoc : undefined);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setTeamMembers(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      approvedTeamMembersQuery(selectedAccount.id),
      (teamMembers) => {
        setTeamMembers(teamMembers.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const updateAccountConfig = useCallback((data: Partial<AccountConfig>) => {
    setAccountConfigToSave((d) => ({
      ...(d || {}),
      ...data,
      seo: { ...(d?.seo || {}), ...data.seo },
    }));
    setAccountConfig((d) => ({
      ...(d || {}),
      ...data,
      seo: { ...(d?.seo || {}), ...data.seo },
    }));
  }, []);

  useEffect(() => {
    if (!selectedAccount || !accountConfigToSave) return;

    const debounce = setTimeout(() => {
      updateAccountConfigApi(selectedAccount.id, accountConfigToSave);
      setAccountConfigToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountConfigToSave, selectedAccount]);

  const setAnalyticsType = useCallback(
    (type?: AnalyticsTypeOption | null) => {
      if (type) {
        localStorage.setItem('analyticsType', type);
      } else {
        localStorage.removeItem('analyticsType');
      }

      _setAnalyticsType(type || null);
    },
    [_setAnalyticsType],
  );

  const getSectionTypeConfig = useCallback(
    (type: SectionType | string) => {
      const onlyMatchingTypes = sectionTypeConfigs?.filter(
        (s) => s.data().type === type,
      );
      return (
        onlyMatchingTypes?.find((s) => s.data().accountId) ||
        onlyMatchingTypes?.[0]
      )?.data();
    },
    [sectionTypeConfigs],
  );

  const getSectionListItemFieldConfig = useCallback(
    (sectionType: SectionType | string, field: string) => {
      const sectionTypeConfig = getSectionTypeConfig(sectionType);
      return sectionTypeConfig?.structure.fields.find(
        (f) => f.type === 'list' && f.name === field,
      ) as SectionTypeStructureFieldList | undefined;
    },
    [getSectionTypeConfig],
  );

  // const fetchDynamicPageWebsitePreviewToken = useCallback(async () => {
  //   if (!selectedAccount || !selectedDynamicPage) {
  //     return;
  //   }
  //   const key = `customPreviewToken-${selectedAccount.id}-${selectedDynamicPage.id}`;
  //   const currentToken = localStorage.getItem(key);

  //   if (currentToken && !isJwtExpired(currentToken)) {
  //     setDynamicPageWebsitePreviewToken(currentToken);
  //     return currentToken;
  //   }

  //   if (tokenFetchingPromises[key]) {
  //     return tokenFetchingPromises[key];
  //   }

  //   const fn = httpsCallable(
  //     functions,
  //     'accounts/campaigns/createPreviewToken',
  //   );

  //   return (tokenFetchingPromises[key] = fn({
  //     accountId: selectedAccount.id,
  //     campaignId: selectedDynamicPage.id,
  //   }).then((res) => {
  //     localStorage.setItem(key, res.data as string);
  //     setDynamicPageWebsitePreviewToken(res.data as string);

  //     return res.data as string;
  //   })).finally(() => {
  //     tokenFetchingPromises[key] = undefined;
  //   });
  // }, [selectedAccount, selectedDynamicPage]);

  return (
    <DashboardContext.Provider
      value={{
        dynamicPages,
        linkedCampaigns,
        isNewAccount,
        // selectedDynamicPage,
        // setSelectedDynamicPage,
        forms,
        quizzes,
        globalSections,
        allDomains,
        domains,
        primaryDomain,
        adsAccounts,
        sectionTypeConfigs,
        getSectionTypeConfig,
        getSectionListItemFieldConfig,
        accountBrand,
        updateAccountBrand,
        accountAiSettings,
        updateAccountAiSettings,
        accountConfig,
        updateAccountConfig,
        subscription,
        hasActiveSubscription,
        analyticsRange,
        setAnalyticsRange,
        analyticsFilters,
        setAnalyticsFilters,
        resetAnalyticsFilters,
        analyticsType,
        setAnalyticsType,
        teamMembers,
        // dynamicPageWebsitePreviewToken,
        // fetchDynamicPageWebsitePreviewToken,
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  );
}

export function useDashboardContext() {
  return useContext(DashboardContext);
}
